import { api } from "../../api/index.ts";
import { useCallback } from "react";
import { DeliveryForm } from "../../types/delivery.ts";
import { deliveryIdToExternal, deliveryUpdateToExternal } from "./convert.ts";
import { useAppDispatch } from "../../redux/store.ts";
import { useView } from "../../contexts/ViewContext.tsx";
import { loadingCellsSlice } from "../../redux/slices/loadingCells.ts";
import { extractApiError } from "../../api/errors.ts";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { useShowToast } from "../../contexts/ToastContext.tsx";

export const useSetDeliveryData = () => {
  const view = useView();
  const showToast = useShowToast();
  const dispatch = useAppDispatch();
  const [triggerUpdate] =
    api.endpoints.updateDeliveryApiDeliveriesSourceDeliveryIdPut.useMutation();
  const [triggerDeleteTransportOrder] =
    api.endpoints.deleteTransportOrderApiTransportOrdersTransportOrderIdDelete.useMutation();
  const [triggerDeleteForwardingOrder] =
    api.endpoints.deleteForwardingOrderApiForwardingOrdersForwardingOrderIdDelete.useMutation();

  const setData = useCallback(
    async (updatedRows: Partial<DeliveryForm>[]) => {
      const loadingCellKeys = updatedRows
        .filter((row) => !!row.id)
        .map((row) =>
          Object.entries(row).reduce(
            (acc, [key]) => [...acc, `${view}.${row.id}.${key}`],
            [] as string[],
          ),
        )
        .flat();
      loadingCellKeys.forEach((key) => {
        dispatch(loadingCellsSlice.actions.cellIsPending({ cell: key }));
      });
      return await Promise.all(
        updatedRows.map((row) => {
          // Deliveries can only be updated, not created
          if (row.id !== undefined) {
            const update = deliveryUpdateToExternal(row);
            if (Object.entries(update).length !== 0) {
              const { id, source } = deliveryIdToExternal(row.id);
              return triggerUpdate({
                deliveryId: id,
                source,
                deliveryUpdate: update,
              });
            } else {
              return undefined;
            }
          } else {
            console.warn("Cannot update delivery without id", row);
            return undefined;
          }
        }),
      )
        .then((results) => {
          console.info("Update results ", results);
          results.forEach((result) => {
            if (result && "error" in result) {
              showToast({
                severity: "error",
                summary: "Błąd",
                detail: extractApiError(
                  result.error as FetchBaseQueryError | SerializedError,
                ),
              });
            }
          });
          return results;
        })
        .catch((err) => {
          console.error(err);
          return [];
        })
        .finally(() => {
          const runningQueries = dispatch(
            api.util.getRunningQueriesThunk(),
          ).filter((query) =>
            query.queryCacheKey.startsWith("listDeliveriesApiDeliveriesGet"),
          );
          // Wait for the latest running query for list endpoint to finish, this avoids "flickering"
          void Promise.all(runningQueries).finally(() => {
            loadingCellKeys.forEach((key) => {
              dispatch(loadingCellsSlice.actions.cellIsLoaded({ cell: key }));
            });
          });
        });
    },
    [triggerUpdate, dispatch, view, showToast],
  );

  // Delete the underlying transport or forwarding order
  const deleteData = useCallback(
    async (deliveryId: number) => {
      try {
        const { id, source } = deliveryIdToExternal(deliveryId);

        if (source === "TransportOrder") {
          const result = await triggerDeleteTransportOrder({
            transportOrderId: id,
          });
          console.info("Deleted transport order", id, result);
          return result;
        } else {
          const result = await triggerDeleteForwardingOrder({
            forwardingOrderId: id,
          });
          console.info("Deleted forwarding order", id, result);
          return result;
        }
      } catch (error) {
        console.error("Delete error", error);
        throw error;
      }
    },
    [triggerDeleteTransportOrder, triggerDeleteForwardingOrder],
  );

  return { setData, deleteData };
};
